import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ResponsiveModal, ResponsiveButton } from '@/components/ui/responsive-modal';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, LogOut, Settings, Calendar, User, CheckCircle, Clock, Trash2, Grid3x3, List, Lock, Unlock, FileText, Download } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

function ProjectList({ user, onLogout }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('projectListViewMode') || 'grid';
  });
  const [newProject, setNewProject] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    customer: '',
  });
  const [csvTemplates, setCsvTemplates] = useState([]);
  const [templateType, setTemplateType] = useState('blank'); // 'blank' or 'csv'
  const [selectedCsvTemplateId, setSelectedCsvTemplateId] = useState('');
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  // ROLLBACK NOTE: Remove this line to disable photo previews
  const [projectPhotos, setProjectPhotos] = useState({}); // Store photos by project ID

  // Helper function to format error messages properly
  const formatErrorMessage = (error, fallbackMessage = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      return detail[0]?.msg || fallbackMessage;
    } else if (typeof detail === 'object' && detail !== null) {
      return detail.msg || fallbackMessage;
    }
    return error.message || fallbackMessage;
  };

  useEffect(() => {
    if (user) {
      loadProjects();
      loadCsvTemplates();
    }
  }, [user]);

  const loadProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
      // ROLLBACK NOTE: Remove the line below to disable photo previews
      loadProjectPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // ROLLBACK NOTE: Remove this entire function to disable photo previews
  const loadProjectPhotos = async (projectsList) => {
    // Only load photos for projects that have photos
    const projectsWithPhotos = projectsList.filter(p => p.photo_count > 0);
    
    // Load photos for each project (limit to first 10 projects with photos)
    const photoPromises = projectsWithPhotos.slice(0, 10).map(async (project) => {
      try {
        const response = await axiosInstance.get(`/projects/${project.id}/photos`);
        // Get only photos that have images (has_photo: true) and limit to 8
        const photosWithImages = response.data
          .filter(photo => photo.has_photo)
          .slice(0, 8);
        
        // Fetch thumbnail blobs for each photo
        const thumbnailPromises = photosWithImages.map(async (photo) => {
          try {
            const photoDetail = await axiosInstance.get(`/photos/by-slot/${photo.id}`);
            const thumbResponse = await axiosInstance.get(`/photos/${photoDetail.data.id}/variant/thumb`, {
              responseType: 'blob'
            });
            return {
              id: photo.id,
              url: URL.createObjectURL(thumbResponse.data)
            };
          } catch (err) {
            return null;
          }
        });
        
        const thumbnails = await Promise.all(thumbnailPromises);
        return {
          projectId: project.id,
          photos: thumbnails.filter(t => t !== null)
        };
      } catch (error) {
        return { projectId: project.id, photos: [] };
      }
    });
    
    const results = await Promise.all(photoPromises);
    const photosMap = {};
    results.forEach(result => {
      if (result.photos.length > 0) {
        photosMap[result.projectId] = result.photos;
      }
    });
    setProjectPhotos(photosMap);
  };

  const loadCsvTemplates = async () => {
    try {
      const response = await axiosInstance.get('/csv-templates');
      setCsvTemplates(response.data);
    } catch (error) {
      // Silently fail - CSV templates are optional
      console.error('Failed to load CSV templates', error);
      setCsvTemplates([]);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = { ...newProject };
      
      // Set blank flag or CSV template ID
      if (templateType === 'blank') {
        projectData.blank = true;
      } else if (templateType === 'csv') {
        if (!selectedCsvTemplateId) {
          toast.error('Please select a CSV template');
          return;
        }
        projectData.csv_template_id = selectedCsvTemplateId;
      }
      
      const response = await axiosInstance.post('/projects', projectData);
      toast.success('Project created successfully!');
      setProjects([response.data, ...projects]);
      setShowCreateModal(false);
      setNewProject({ name: '', date: new Date().toISOString().split('T')[0], customer: '' });
      setTemplateType('blank');
      setSelectedCsvTemplateId('');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to create project'));
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteProjectId) return;
    
    try {
      await axiosInstance.delete(`/projects/${deleteProjectId}`);
      toast.success('Project deleted successfully');
      setProjects(projects.filter(p => p.id !== deleteProjectId));
      setDeleteProjectId(null);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete project'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      complete: { label: 'Complete', class: 'status-complete', icon: CheckCircle },
      in_progress: { label: 'In Progress', class: 'status-in-progress', icon: Clock },
      draft: { label: 'Draft', class: 'status-draft', icon: Clock },
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`status-badge ${badge.class} flex items-center gap-1`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex md:hidden items-center py-3 gap-2">
            <div className="flex flex-col flex-shrink-0">
              <h1 className="text-sm font-bold text-gray-900 whitespace-nowrap">JobPro Photos</h1>
              <p className="text-xs text-gray-500 whitespace-nowrap">{user.full_name || user.username}</p>
            </div>
            <div className="flex-1 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-user-portal-2/artifacts/9fookd3l_Logo%20Datastorm%20Blue%20with%20Transparent%20BG%20Cropped.png" 
                alt="Datastorm Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {user.role === 'admin' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin')}
                  data-testid="admin-panel-button"
                  className="flex items-center gap-2 text-sm"
                  size="sm"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="logout-button" className="flex items-center gap-2 text-sm" size="sm">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will be logged out of your account.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:grid grid-cols-3 items-center py-3 gap-4">
            <div className="flex flex-col justify-center">
              <h1 className="text-xl font-bold text-gray-900">JobPro Photos</h1>
              <p className="text-sm text-gray-500">Welcome, {user.full_name || user.username}</p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-user-portal-2/artifacts/9fookd3l_Logo%20Datastorm%20Blue%20with%20Transparent%20BG%20Cropped.png" 
                alt="Datastorm Logo" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              {user.role === 'admin' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin')}
                  data-testid="admin-panel-button"
                  className="flex items-center gap-2 text-sm"
                  size="sm"
                >
                  <Settings className="w-4 h-4" />
                  <span>Admin</span>
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="logout-button-desktop" className="flex items-center gap-2 text-sm" size="sm">
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will be logged out of your account.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Projects</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setViewMode('grid');
                  localStorage.setItem('projectListViewMode', 'grid');
                }}
                className="h-7 w-7 p-0"
                style={{ minHeight: '28px', minWidth: '28px' }}
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setViewMode('list');
                  localStorage.setItem('projectListViewMode', 'list');
                }}
                className="h-7 w-7 p-0"
                style={{ minHeight: '28px', minWidth: '28px' }}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
            
            <Button
              data-testid="create-project-button"
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden md:inline">New Project</span>
              <span className="md:hidden">New</span>
            </Button>
            
            <ResponsiveModal
              open={showCreateModal}
              onOpenChange={setShowCreateModal}
              title="Create New Project"
              maxWidth="sm:max-w-lg"
              mobileHeight="90vh"
            >
              <form onSubmit={handleCreateProject} className="space-y-4 pb-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    data-testid="project-name-input"
                    placeholder="e.g., Office Building Inspection"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                    className="text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    data-testid="project-date-input"
                    type="date"
                    value={newProject.date}
                    onChange={(e) => setNewProject({ ...newProject, date: e.target.value })}
                    required
                    className="text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer">Customer</Label>
                  <Input
                    id="customer"
                    data-testid="project-customer-input"
                    placeholder="Customer name (optional)"
                    value={newProject.customer}
                    onChange={(e) => setNewProject({ ...newProject, customer: e.target.value })}
                    className="text-base"
                  />
                </div>
                
                {/* Template Type Selector */}
                <div className="space-y-3 pt-2 border-t">
                  <Label>Project Type</Label>
                  
                  {/* Blank Project Option */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="template-blank"
                        name="template-type"
                        checked={templateType === 'blank'}
                        onChange={() => setTemplateType('blank')}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="template-blank" className="cursor-pointer font-normal">
                        Start Blank Project
                      </Label>
                    </div>
                    <p className="text-xs text-gray-500 ml-7">
                      Creates a project with one empty "Photos" category
                    </p>
                  </div>
                  
                  {/* CSV Template Option */}
                  {csvTemplates.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="template-csv"
                          name="template-type"
                          checked={templateType === 'csv'}
                          onChange={() => setTemplateType('csv')}
                          className="w-5 h-5"
                        />
                        <Label htmlFor="template-csv" className="cursor-pointer font-normal">
                          Use CSV Template
                        </Label>
                      </div>
                      
                      {templateType === 'csv' && (
                        <div className="ml-7 space-y-2">
                          <select
                            value={selectedCsvTemplateId}
                            onChange={(e) => setSelectedCsvTemplateId(e.target.value)}
                            className="w-full border rounded-md p-2 text-base"
                            required={templateType === 'csv'}
                          >
                            <option value="">Select a CSV template...</option>
                            {csvTemplates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name} ({template.category_count} categories, {template.slot_count} slots)
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500">
                            Choose from uploaded CSV templates
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Download Boilerplate Link */}
                  <a
                    href={`${API}/csv-templates/download-boilerplate`}
                    download
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 ml-7"
                  >
                    <Download className="w-3 h-3" />
                    Download CSV Template Example
                  </a>
                </div>
                
                <ResponsiveButton type="submit" data-testid="create-project-submit" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </ResponsiveButton>
              </form>
            </ResponsiveModal>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <Card className="text-center py-12" data-testid="empty-state">
            <CardContent>
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-user-portal-2/artifacts/9fookd3l_Logo%20Datastorm%20Blue%20with%20Transparent%20BG%20Cropped.png" 
                alt="Datastorm Logo" 
                className="w-32 h-auto mx-auto mb-4 opacity-40"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects yet</h3>
              <p className="text-gray-600 mb-4">Create your first project to get started</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="card-hover border border-gray-200 hover:border-blue-300 transition-all relative group"
              >
                {/* Delete button (admin only, hidden on mobile) */}
                {user.role === 'admin' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteProjectId(project.id);
                    }}
                    className="hidden md:block absolute bottom-2 right-2 z-10 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                    title="Delete project"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                )}
                
                <div onClick={() => navigate(`/projects/${project.id}`)} className="cursor-pointer">
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <CardTitle className="text-lg font-bold text-gray-900 line-clamp-2">{project.name}</CardTitle>
                        {project.locked ? (
                          <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" title="Project is locked" />
                        ) : (
                          <Unlock className="w-4 h-4 text-gray-400 flex-shrink-0" title="Project is unlocked" />
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(project.status)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-4">
                      {/* Left side - Date and Customer info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(project.date).toLocaleDateString()}</span>
                        </div>
                        {project.customer && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <User className="w-4 h-4" />
                            <span>{project.customer}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* ROLLBACK NOTE: Remove this entire block (lines with "Right side - Photo Preview") to disable photo previews */}
                      {/* Right side - Photo Preview - Horizontally Stacked Cards with 3D Effect */}
                      {projectPhotos[project.id] && projectPhotos[project.id].length > 0 && (
                        <div className="flex relative h-12" style={{ width: `${Math.max(70, 40 + (projectPhotos[project.id].length - 1) * 20)}px`, perspective: '400px' }}>
                          {projectPhotos[project.id].slice(0, 8).map((photo, idx) => {
                            // Calculate Y-axis rotation for 3D effect (cards "turn" slightly)
                            const rotationY = -12 + (idx * 3); // Start at -12deg, increase by 3deg per card
                            
                            return (
                              <div 
                                key={photo.id}
                                className="absolute top-0 w-10 h-12 rounded overflow-hidden border-2 border-white shadow-lg bg-gray-100"
                                style={{
                                  left: `${idx * 20}px`, // 20px overlap spacing
                                  zIndex: 10 - idx, // First image on top
                                  transform: `rotateY(${rotationY}deg)`, // 3D Y-axis rotation
                                  transformStyle: 'preserve-3d',
                                }}
                                title={`Photo ${idx + 1}`}
                              >
                                <img 
                                  src={photo.url} 
                                  alt="" 
                                  className="w-full h-full object-cover pointer-events-none"
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="pt-3 border-t border-gray-100 space-y-1">
                      <div className="flex items-center text-xs text-gray-500">
                        <span>Photos: {project.photo_count || 0} / {project.slot_count}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((project) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="card-hover cursor-pointer border border-gray-200 hover:border-blue-300 transition-all"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 truncate">{project.name}</h3>
                          {project.locked ? (
                            <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" title="Project is locked" />
                          ) : (
                            <Unlock className="w-4 h-4 text-gray-400 flex-shrink-0" title="Project is unlocked" />
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {getStatusBadge(project.status)}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(project.date).toLocaleDateString()}</span>
                        </div>
                        {project.customer && (
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>{project.customer}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Photos: {project.photo_count || 0} / {project.slot_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProjectId} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this project? This action will move the project to the trash. You can restore it later from the Admin Panel's Deleted Projects section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ProjectList;
